import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Logo from "@/components/Logo";

const VenueAdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Credentials are verified SERVER-SIDE; the password hash never reaches
      // the browser. host_login() also downgrades an expired plan to basic and
      // returns an opaque session token on success.
      const { data, error } = await (supabase as any).rpc('host_login', {
        p_username: username.trim(),
        p_password: password,
        p_account_type: 'venue_admin',
      });

      const row = Array.isArray(data) ? data[0] : data;

      if (error || !row) {
        const msg = String(error?.message ?? '');
        if (msg.includes('too_many_attempts')) {
          toast({
            title: "Too Many Attempts",
            description: "Too many failed logins. Please wait about 15 minutes and try again.",
            variant: "destructive",
          });
        } else if (msg.includes('account_inactive')) {
          toast({
            title: "Account Inactive",
            description: "Your account has been deactivated. Please contact info@nairoke.com",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Invalid Credentials",
            description: "Username or password is incorrect",
            variant: "destructive",
          });
        }
        return;
      }

      if (row.subscription_end_date && new Date(row.subscription_end_date) <= new Date()) {
        toast({
          title: "Subscription Expired",
          description: "Your plan has expired. You've been moved to the Basic (trial) tier.",
          variant: "destructive",
        });
      }

      localStorage.setItem('hostSessionToken', row.token);
      localStorage.setItem('hostAuth', 'true');
      localStorage.setItem('hostId', row.host_id);
      localStorage.setItem('userRole', 'venue_admin');
      localStorage.setItem('accountType', 'venue_admin');
      if (row.venue_id) {
        localStorage.setItem('currentVenueId', row.venue_id);
      }

      toast({
        title: "Welcome back!",
        description: `Logged in as ${row.display_name}`,
      });
      navigate('/host-dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Login Failed",
        description: "An error occurred during login",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist relative">
      {/* Faint 48px grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
        }}
      />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="border-b border-console-line bg-console-bg/85 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-3 min-h-[44px]">
              <div className="relative">
                <Logo tone="text" size="md" />
                <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-console-primary shadow-[0_0_6px_#4D7CFE]" />
              </div>
              <div className="flex flex-col leading-none text-left">
                <span className="font-geist font-semibold text-[15px] tracking-tight text-console-text">KaraokeCore</span>
                <span className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted mt-1">Venue admin</span>
              </div>
            </button>

            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="h-11 sm:h-9 px-3 sm:px-4 text-[13px] font-medium font-geist text-console-text hover:bg-white/5 hover:text-console-text border border-console-line-strong rounded-[6px]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </header>

        {/* Form */}
        <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md">
            <div className="bg-console-surface border border-console-line rounded-[12px] p-7 sm:p-9">
              <div className="font-geist-mono text-[11px] tracking-[0.18em] uppercase text-console-primary mb-4">
                02 / Venue admin
              </div>
              <h1 className="font-geist font-semibold text-[36px] leading-none tracking-[-0.03em] mb-3">
                Host login
              </h1>
              <p className="font-geist text-[14px] leading-relaxed text-console-muted mb-7">
                Open your venue's console — queue, library, displays, and the floor.
              </p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="username" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">
                    Username
                  </Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="your username"
                    className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist text-[15px] rounded-[6px] focus:border-console-primary focus-visible:ring-0 focus-visible:ring-offset-0"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist text-[15px] rounded-[6px] focus:border-console-primary focus-visible:ring-0 focus-visible:ring-offset-0"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 px-5 mt-3 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]"
                  disabled={loading}
                >
                  {loading ? "Signing in…" : "Open console"}
                  <span className="font-geist-mono text-[11px] opacity-70 ml-2">→</span>
                </Button>
                <button
                  type="button"
                  onClick={() => navigate('/reset-password')}
                  className="w-full text-center font-geist-mono text-[11px] text-console-dim hover:text-console-muted underline underline-offset-2"
                >
                  Forgot password?
                </button>
              </form>
            </div>

            <div className="mt-6 text-center font-geist-mono text-[11px] tracking-[0.05em] text-console-dim">
              Show host?{" "}
              <button
                onClick={() => navigate('/show-host')}
                className="text-console-signal hover:text-console-signal/80 underline underline-offset-2"
              >
                Sign in here
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default VenueAdminLogin;
